import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import {
    ModalOverlay,
    ModalContainer,
    AuthForm,
    AuthInput,
    AuthButton,
    Separator,
    TabRow,
    Tab,
    ToggleButton
} from '../styles/AuthStyles';
import { ErrorMessage } from '../styles/styled';
import { theme } from '../styles/theme';
import { friendlyAuthError } from '../utils/authErrors';

const InfoMessage = styled.div`
    color: ${theme.colors.success};
    background: rgba(46, 213, 115, 0.1);
    border: 1px solid ${theme.colors.success};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: 0.9rem;
    margin-bottom: ${theme.spacing.md};
    text-align: center;
`;

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialMode?: 'signin' | 'signup';
    onSignupSuccess?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signin', onSignupSuccess }) => {
    const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [usePhone, setUsePhone] = useState(false);
    const [showVerification, setShowVerification] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    const { signInEmail, signUpEmail, resetPassword, signInPhone, setUpRecaptcha } = useAuth();

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setInfo('');

        try {
            if (usePhone) {
                if (showVerification) {
                    setError('Please complete verification.');
                } else {
                    setUpRecaptcha('recaptcha-container');
                    await signInPhone(phone);
                    setShowVerification(true);
                }
            } else {
                if (isSignUp) {
                    await signUpEmail(email, password);
                    if (onSignupSuccess) onSignupSuccess();
                } else {
                    await signInEmail(email, password);
                }
                onClose();
            }
        } catch (err: any) {
            setError(friendlyAuthError(err));
        }
    };

    const handleResetPassword = async () => {
        setError('');
        setInfo('');
        if (!email.trim()) {
            setError('Enter your email above first, then tap "Forgot password?".');
            return;
        }
        try {
            await resetPassword(email.trim());
            setInfo(`If an account exists for ${email.trim()}, a reset link is on the way.`);
        } catch (err: any) {
            setError(friendlyAuthError(err));
        }
    };

    const switchMode = (next: boolean) => {
        if (next === isSignUp) return;
        setIsSignUp(next);
        setError('');
        setInfo('');
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={e => e.stopPropagation()}>
                <TabRow role="tablist">
                    <Tab
                        type="button"
                        role="tab"
                        aria-selected={!isSignUp}
                        $active={!isSignUp}
                        onClick={() => switchMode(false)}
                    >
                        Sign In
                    </Tab>
                    <Tab
                        type="button"
                        role="tab"
                        aria-selected={isSignUp}
                        $active={isSignUp}
                        onClick={() => switchMode(true)}
                    >
                        Sign Up
                    </Tab>
                </TabRow>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {info && <InfoMessage>{info}</InfoMessage>}

                <AuthForm onSubmit={handleSubmit}>
                    {!usePhone ? (
                        <>
                            <AuthInput
                                type="email"
                                placeholder="Email Address"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                            <AuthInput
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                            />
                        </>
                    ) : (
                        <>
                            <AuthInput
                                type="tel"
                                placeholder="Phone Number (+1...)"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                disabled={showVerification}
                            />
                            {showVerification && (
                                <AuthInput
                                    type="text"
                                    placeholder="Verification Code"
                                    value={verificationCode}
                                    onChange={e => setVerificationCode(e.target.value)}
                                />
                            )}
                            <div id="recaptcha-container"></div>
                        </>
                    )}

                    <AuthButton type="submit">
                        {usePhone
                            ? (showVerification ? 'Verify Code' : 'Send Code')
                            : (isSignUp ? 'Sign Up' : 'Sign In')}
                    </AuthButton>
                </AuthForm>

                {!isSignUp && !usePhone && (
                    <ToggleButton type="button" onClick={handleResetPassword}>
                        Forgot password?
                    </ToggleButton>
                )}

                <Separator><span>OR</span></Separator>

                <AuthButton
                    style={{ background: '#333' }}
                    onClick={() => { setUsePhone(!usePhone); setError(''); }}
                    type="button"
                >
                    {usePhone ? 'Use Email' : 'Use Phone Number'}
                </AuthButton>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default AuthModal;
